import { useEffect } from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { CheckmarkIcon, ChevronIcon } from "vega-icons";

import { useStore } from "@nanostores/react";
import {
  $organization,
  $organizationList,
  organisationActions,
} from "@vega-api/custom/stores/organisations.store";
import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import { $sidebarCollapsed } from "@vega-core/components/Navigation/Sidebar/Sidebar.store";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { CommonPaths, VegaModulesPaths } from "../../paths";
import { SettingsSection } from "../../scenes/Settings/constants";
import { mobileNavigationActions } from "../MobileNavigation/MobileNavigation.store";

import { OrganisationAvatarName } from "./OrganisationAvatarName";

type Props = {
  hideLabel?: boolean;
  isSidebar?: boolean;
};

export const SelectOrganisation = ({
  hideLabel = false,
  isSidebar = true,
}: Props) => {
  const selectedOrganisation = useStore($organization);
  const { isOpen, setIsOpen } = useBoolean(false);

  const organisationsList = useStore($organizationList);
  const setOrganisation = organisationActions.setOrganisation;
  const isCollapsed = useStore($sidebarCollapsed);
  const isMobile = !useDeviceWidth("md");
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const orgAgnosticRoutes = [
    CommonPaths.Home,
    CommonPaths.SelectOrg,
    VegaModulesPaths.ResourcesRoot,
    VegaModulesPaths.ResourcesCms,
    VegaModulesPaths.CapitalActivity,
    VegaModulesPaths.InvestorDocuments,
    VegaModulesPaths.Offerings,
    VegaModulesPaths.Settings,
    VegaModulesPaths.SettingsPersonalDetails,
    VegaModulesPaths.SettingsDisplay,
    VegaModulesPaths.SettingsNotifications,
    VegaModulesPaths.SettingsSupport,
    `${VegaModulesPaths.Settings}/${SettingsSection.Users}`,
    `${VegaModulesPaths.Settings}/${SettingsSection.MyAccess}`,
    `${VegaModulesPaths.Settings}/${SettingsSection.LoginHistory}`,
    VegaModulesPaths.CoInvestments,
  ];

  const isOrgAgnosticRoute = orgAgnosticRoutes.some((route) =>
    matchPath(route, location.pathname),
  );

  const { featureFlag } = useFeatureFlag();
  const isOrgUrlSyncEnabled = featureFlag.lp_organization_url_sync;
  const isSwitcherAllPagesEnabled = featureFlag.lp_org_switcher_all_pages;

  const getRedirectPathForCurrentRoute = () => {
    if (matchPath(VegaModulesPaths.FundDetailsOpportunity, location.pathname)) {
      return VegaModulesPaths.Offerings;
    }

    return CommonPaths.Home;
  };

  const organizationIdFromUrl = searchParams.get("organizationId");

  // Sync organization between URL and store
  useEffect(() => {
    if (!isOrgUrlSyncEnabled) {
      return;
    }

    if (organizationIdFromUrl !== selectedOrganisation?.id) {
      // URL has org ID but store doesn't match - restore from URL (bookmark case)
      if (organizationIdFromUrl && organisationsList.length > 0) {
        const orgFromUrl = organisationsList.find(
          (org) => org.id === organizationIdFromUrl,
        );

        if (orgFromUrl) {
          setOrganisation(orgFromUrl);

          return;
        }
      }

      // Store has org ID but URL doesn't match - update URL
      if (selectedOrganisation?.id) {
        setSearchParams(
          (prev) => {
            prev.set("organizationId", selectedOrganisation.id);

            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [
    isOrgUrlSyncEnabled,
    organizationIdFromUrl,
    organisationsList,
    selectedOrganisation?.id,
    setOrganisation,
    setSearchParams,
  ]);

  const handleOrgSelect = (org: NonNullable<typeof selectedOrganisation>) => {
    if (!isSwitcherAllPagesEnabled && !isOrgAgnosticRoute) {
      return;
    }

    setOrganisation(org);

    if (isSwitcherAllPagesEnabled && !isOrgAgnosticRoute) {
      const redirectPath = getRedirectPathForCurrentRoute();
      const search = isOrgUrlSyncEnabled ? `?organizationId=${org.id}` : "";
      navigate(`${redirectPath}${search}`);
    } else if (isOrgUrlSyncEnabled) {
      setSearchParams(
        (prev) => {
          prev.set("organizationId", org.id);

          return prev;
        },
        { replace: true },
      );
    }

    mobileNavigationActions.closeAll();
    setIsOpen(false);
  };

  const getNavLabel = () =>
    (isCollapsed && !isMobile) || hideLabel ? null : (
      <div
        className={cnTwMerge(
          "heading-6 mb-2 pl-4",
          isSidebar ? "text-text/sidebar/secondary" : "text-text/secondary",
        )}
      >
        {m.navTitles_organization()}
      </div>
    );

  if (organisationsList.length === 1) {
    return (
      <>
        {getNavLabel()}
        <div
          className={cnTwMerge(
            "h-11 text-ellipsis justify-start group flex items-center rounded-companyBorderRadius text-sm font-semibold relative gap-3 px-3",
            isSidebar ? "text-text/sidebar/primary" : "text-text/primary",
          )}
        >
          <OrganisationAvatarName name={selectedOrganisation?.name} />
        </div>
      </>
    );
  }

  return (
    <>
      {getNavLabel()}
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <PopoverTrigger asChild>
          <div
            className={cnTwMerge(
              "h-11 text-ellipsis hover:bg-background/component justify-center group flex items-center rounded-companyBorderRadius text-sm font-semibold relative cursor-pointer gap-3 px-3",
              isSidebar ? "text-text/sidebar/primary" : "text-text/primary",
              {
                "gap-2": isCollapsed,
              },
            )}
          >
            <OrganisationAvatarName name={selectedOrganisation?.name} />
            <ChevronIcon className="ml-auto min-h-6 min-w-6 block" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="p-0"
          data-testid={`${selectedOrganisation?.name}-popover`}
          side="bottom"
        >
          <div className="space-y-0.5 p-2">
            {organisationsList.map((org) => (
              <div
                className={cnTwMerge(
                  "flex gap-4 h-11 text-ellipsis text-text/primary hover:bg-background/component justify-start group items-center rounded-companyBorderRadius px-3 py-2.5 relative cursor-pointer",
                  selectedOrganisation?.id === org.id && "bg-muted",
                  {
                    "cursor-not-allowed grayscale":
                      !isSwitcherAllPagesEnabled && !isOrgAgnosticRoute,
                  },
                )}
                data-testid={org.id}
                key={org.id}
                onClick={() => handleOrgSelect(org)}
              >
                <OrganisationAvatarName name={org.name} />
                <div className="w-6 h-6 ml-auto relative">
                  <CheckmarkIcon
                    className={cnTwMerge(
                      "w-6 h-6 text-brand-default absolute inset-0",
                      selectedOrganisation?.id === org.id
                        ? "block group-hover:hidden"
                        : "hidden",
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};
